import { useCallback, useEffect, useState } from 'react'
import { supabase } from '../lib/supabase'
import {
  classifySyncSource, overallSyncStatus,
  type SyncSourceHealth, type SyncStatus, type SyncSourceInput,
} from '../lib/syncHealth'

/**
 * Saúde do sync por fonte (Obj 1 — consolidar estoque com precisão). Lê o registro
 * mais recente de cada tabela-fonte (RLS já escopa por empresa) e classifica o
 * frescor. Fontes com cron regular (saldo/vendas) têm janela curta; fontes
 * incrementais/esporádicas (notas/fornecedor) têm janela generosa — um gap longo
 * ali é normal, não é sync parado.
 */

interface SourceSpec {
  key: string
  label: string
  table: string
  column: string
  staleAfterH: number
  offlineAfterH: number
  hint: string
}

const SPECS: SourceSpec[] = [
  { key: 'saldo',      label: 'Saldo (estoque)',   table: 'products',          column: 'last_stock_sync_at',  staleAfterH: 6,   offlineAfterH: 24,  hint: 'Último saldo sincronizado do Tiny.' },
  { key: 'vendas',     label: 'Vendas',            table: 'sales',             column: 'synced_at',           staleAfterH: 12,  offlineAfterH: 48,  hint: 'Última venda importada — base da média de consumo.' },
  { key: 'notas',      label: 'Notas de entrada',  table: 'purchase_invoices', column: 'updated_at',          staleAfterH: 168, offlineAfterH: 720, hint: 'Última nota de entrada (custo/boletos). Gaps longos são normais.' },
  { key: 'fornecedor', label: 'Fornecedor',        table: 'products',          column: 'supplier_synced_at',  staleAfterH: 168, offlineAfterH: 720, hint: 'Último vínculo de fornecedor puxado do Tiny.' },
]

export interface SyncHealthState {
  sources: SyncSourceHealth[]
  overall: SyncStatus
  loading: boolean
  error: string | null
  refresh: () => Promise<void>
}

async function latestTimestamp(table: string, column: string): Promise<string | null> {
  const { data, error } = await supabase
    .from(table)
    .select(column)
    .not(column, 'is', null)
    .order(column, { ascending: false })
    .limit(1)
    .maybeSingle()
  if (error) return null
  return (data as Record<string, string> | null)?.[column] ?? null
}

export function useSyncHealth(): SyncHealthState {
  const [sources, setSources] = useState<SyncSourceHealth[]>([])
  const [overall, setOverall] = useState<SyncStatus>('fresh')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // compute() NÃO faz setState — só busca e classifica. Quem aplica é o effect
  // (dentro de um IIFE async) ou o handler do botão, evitando setState síncrono
  // no corpo do effect (react-hooks/set-state-in-effect).
  const compute = useCallback(async (): Promise<
    { ok: true; health: SyncSourceHealth[]; overall: SyncStatus } | { ok: false; error: string }
  > => {
    try {
      const nowMs = Date.now()
      const lasts = await Promise.all(SPECS.map(s => latestTimestamp(s.table, s.column)))
      const health = SPECS.map((s, i) => classifySyncSource({
        key: s.key, label: s.label, lastAt: lasts[i],
        staleAfterH: s.staleAfterH, offlineAfterH: s.offlineAfterH, hint: s.hint,
      } satisfies SyncSourceInput, nowMs))
      return { ok: true, health, overall: overallSyncStatus(health) }
    } catch (e) {
      return { ok: false, error: e instanceof Error ? e.message : 'Erro ao carregar saúde do sync' }
    }
  }, [])

  // Botão manual: mostra "Verificando…" na hora (setState em handler é permitido).
  const refresh = useCallback(async () => {
    setLoading(true)
    const r = await compute()
    if (r.ok) { setSources(r.health); setOverall(r.overall); setError(null) }
    else setError(r.error)
    setLoading(false)
  }, [compute])

  useEffect(() => {
    let cancelled = false
    void (async () => {
      const r = await compute()
      if (cancelled) return
      if (r.ok) { setSources(r.health); setOverall(r.overall); setError(null) }
      else setError(r.error)
      setLoading(false)
    })()
    return () => { cancelled = true }
  }, [compute])

  return { sources, overall, loading, error, refresh }
}
